import { Suspense, lazy, type ReactNode } from 'react';
import { Navigate, createBrowserRouter } from 'react-router-dom';
import { UserRole } from '@docsystem/shared';
import { AppShell } from '@/components/layout/AppShell';
import { RoleGuard } from '@/components/layout/RoleGuard';
import { Spinner } from '@/components/ui/misc';

const LoginPage = lazy(() => import('@/app/login/LoginPage'));
const ChamberLoginPage = lazy(() => import('@/app/chamber-login/ChamberLoginPage'));
const DisplayScreen = lazy(() => import('@/app/display/DisplayScreen'));
const ScreenLoginPage = lazy(() => import('@/app/display/ScreenLoginPage'));
const ChamberDisplayScreen = lazy(() => import('@/app/display/ChamberDisplayScreen'));

const ReceptionDashboard = lazy(() => import('@/app/reception/ReceptionDashboard'));
const ReceptionQueue = lazy(() => import('@/app/reception/ReceptionQueue'));
const PatientsPage = lazy(() => import('@/app/shared/PatientsPage'));
const AppointmentsPage = lazy(() => import('@/app/shared/AppointmentsPage'));

const DoctorDashboard = lazy(() => import('@/app/doctor/DoctorDashboard'));
const ConsultationPage = lazy(() => import('@/app/doctor/ConsultationPage'));

const AdminDashboard = lazy(() => import('@/app/admin/AdminDashboard'));
const AdminDoctors = lazy(() => import('@/app/admin/AdminDoctors'));
const AdminChambers = lazy(() => import('@/app/admin/AdminChambers'));
const AdminSchedules = lazy(() => import('@/app/admin/AdminSchedules'));
const AdminUsers = lazy(() => import('@/app/admin/AdminUsers'));
const AdminDisplays = lazy(() => import('@/app/admin/AdminDisplays'));
const AdminReports = lazy(() => import('@/app/admin/AdminReports'));
const AdminAuditLogs = lazy(() => import('@/app/admin/AdminAuditLogs'));
const AdminSettings = lazy(() => import('@/app/admin/AdminSettings'));
const AdminQueues = lazy(() => import('@/app/admin/AdminQueues'));

function Loader() {
  return (
    <div className="flex min-h-[50vh] items-center justify-center">
      <Spinner className="h-6 w-6 text-primary" />
    </div>
  );
}

function Page({ children }: { children: ReactNode }) {
  return <Suspense fallback={<Loader />}>{children}</Suspense>;
}

function Guarded({ allow, children }: { allow: UserRole[]; children: ReactNode }) {
  return (
    <RoleGuard allow={allow}>
      <Page>{children}</Page>
    </RoleGuard>
  );
}

const STAFF = [UserRole.ADMIN, UserRole.RECEPTIONIST];
const CLINICAL = [UserRole.ADMIN, UserRole.RECEPTIONIST, UserRole.DOCTOR];

export const router = createBrowserRouter([
  { path: '/', element: <Navigate to="/login" replace /> },

  {
    path: '/login',
    element: (
      <Page>
        <LoginPage />
      </Page>
    ),
  },

  {
    path: '/chamber-login',
    element: (
      <Page>
        <ChamberLoginPage />
      </Page>
    ),
  },

  // The display routes have no app shell, no navigation and their own credential —
  // a kiosk must not be one stray click away from the admin area.
  //
  // A screen is installed by opening /display and signing in as its room. No token
  // travels in a URL: the `?dt=` provisioning link was removed, because a link that
  // carries a credential gets forwarded and screenshotted and every copy keeps working.
  {
    path: '/display',
    element: (
      <Page>
        <ScreenLoginPage />
      </Page>
    ),
  },

  {
    path: '/display/:doctorId',
    element: (
      <Page>
        <DisplayScreen />
      </Page>
    ),
  },

  {
    path: '/display/chamber/:chamberId',
    element: (
      <Page>
        <ChamberDisplayScreen />
      </Page>
    ),
  },

  // ── Reception ──────────────────────────────────────────────────────────
  {
    path: '/reception',
    element: (
      <RoleGuard allow={STAFF}>
        <AppShell />
      </RoleGuard>
    ),
    children: [
      { index: true, element: <Navigate to="/reception/dashboard" replace /> },
      { path: 'dashboard', element: <Guarded allow={STAFF}><ReceptionDashboard /></Guarded> },
      { path: 'queue', element: <Guarded allow={STAFF}><ReceptionQueue /></Guarded> },
      { path: 'patients', element: <Guarded allow={CLINICAL}><PatientsPage /></Guarded> },
      { path: 'appointments', element: <Guarded allow={CLINICAL}><AppointmentsPage /></Guarded> },
    ],
  },

  // ── Doctor ─────────────────────────────────────────────────────────────
  {
    path: '/doctor',
    element: (
      <RoleGuard allow={[UserRole.ADMIN, UserRole.DOCTOR]}>
        <AppShell />
      </RoleGuard>
    ),
    children: [
      { index: true, element: <Navigate to="/doctor/dashboard" replace /> },
      {
        path: 'dashboard',
        element: (
          <Guarded allow={[UserRole.ADMIN, UserRole.DOCTOR]}>
            <DoctorDashboard />
          </Guarded>
        ),
      },
      {
        path: 'consultation/:tokenId',
        element: (
          <Guarded allow={[UserRole.ADMIN, UserRole.DOCTOR]}>
            <ConsultationPage />
          </Guarded>
        ),
      },
      {
        path: 'appointments',
        element: (
          <Guarded allow={[UserRole.ADMIN, UserRole.DOCTOR]}>
            <AppointmentsPage />
          </Guarded>
        ),
      },
    ],
  },

  // ── Admin ──────────────────────────────────────────────────────────────
  {
    path: '/admin',
    element: (
      <RoleGuard allow={[UserRole.ADMIN]}>
        <AppShell />
      </RoleGuard>
    ),
    children: [
      { index: true, element: <Navigate to="/admin/dashboard" replace /> },
      { path: 'dashboard', element: <Guarded allow={[UserRole.ADMIN]}><AdminDashboard /></Guarded> },
      { path: 'queues', element: <Guarded allow={[UserRole.ADMIN]}><AdminQueues /></Guarded> },
      { path: 'appointments', element: <Guarded allow={[UserRole.ADMIN]}><AppointmentsPage /></Guarded> },
      { path: 'patients', element: <Guarded allow={[UserRole.ADMIN]}><PatientsPage /></Guarded> },
      { path: 'doctors', element: <Guarded allow={[UserRole.ADMIN]}><AdminDoctors /></Guarded> },
      { path: 'chambers', element: <Guarded allow={[UserRole.ADMIN]}><AdminChambers /></Guarded> },
      { path: 'schedules', element: <Guarded allow={[UserRole.ADMIN]}><AdminSchedules /></Guarded> },
      { path: 'displays', element: <Guarded allow={[UserRole.ADMIN]}><AdminDisplays /></Guarded> },
      { path: 'users', element: <Guarded allow={[UserRole.ADMIN]}><AdminUsers /></Guarded> },
      { path: 'reports', element: <Guarded allow={[UserRole.ADMIN]}><AdminReports /></Guarded> },
      { path: 'audit-logs', element: <Guarded allow={[UserRole.ADMIN]}><AdminAuditLogs /></Guarded> },
      { path: 'settings', element: <Guarded allow={[UserRole.ADMIN]}><AdminSettings /></Guarded> },
    ],
  },

  { path: '*', element: <Navigate to="/login" replace /> },
]);
